import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ContentContainer,
  DefaultButton,
  Dropdown,
  ICON_PROPS,
  IconButton,
  MainContentContainer,
  ObjectItem,
  ObjectList,
  ObjectListHeader,
  PrimaryButton,
  TextField,
} from "@riag-libs/etax-pattern-library";
import {
  Dialog,
  DialogFooter,
  DialogType,
  FontIcon,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Text,
  TooltipHost,
} from "@fluentui/react";
import HttpService from "../../services/HttpService";
import { NotActiveMessage } from "./NotActiveMessage";
import { NotActiveSteuerKonto } from "./NotActiveSteuerKonto";
import { newETaxDeclaration } from "../../helpers/helpers";
import { SnapsharePanel } from "../Panel/SnapsharePanel";
import {
  NEWEST_SELECTABLE_TAX_YEAR,
  SELECTABLE_TAX_YEARS,
} from "../../constants/constants";

export const BelegBox = (props) => {
  const { mandant, setMandant } = props;
  const [searchText, setSearchText] = useState("");
  const [selectedTaxYear, setSelectedTaxYear] = useState("all");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editTaxYear, setEditTaxYear] = useState("");
  const [editFileName, setEditFileName] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [documentIdToDelete, setDocumentIdToDelete] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const [fullDocumentUrls, setFullDocumentUrls] = useState({});
  const [loadingFullDocument, setLoadingFullDocument] = useState(false);
  const [isSnapsharePanelOpen, setIsSnapsharePanelOpen] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);

  const fileInputRef = useRef(null);

  const fetchDocumentThumbnail = useCallback(
    async (documentId) => {
      try {
        const response = await HttpService.getAxiosClient().get(
          `/api/documents/thumbnail/${mandant.SubjektId}/${documentId}`,
          {
            responseType: "blob",
          }
        );

        if (response.status === 200) {
          const thumbnailUrl = URL.createObjectURL(response.data);
          setThumbnails((prev) => ({
            ...prev,
            [documentId]: thumbnailUrl,
          }));
        }
      } catch (error) {
        console.error(
          `Error fetching document for thumbnail ${documentId}:`,
          error
        );
      }
    },
    [setThumbnails]
  );

  const fetchDocuments = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      try {
        if (!silent) {
          setLoadingDocuments(true);
        }
        const requestBody = {
          SubjektId: parseInt(mandant.SubjektId),
          filter: null,
          page: null,
          sort: null,
        };
        const response = await HttpService.getAxiosClient().post(
          "/api/documents/list",
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          const docs = response.data.documents || [];
          setDocuments(docs);
          docs.forEach((doc) => {
            fetchDocumentThumbnail(doc.document_id);
          });
        }
      } catch (error) {
        console.error("Error fetching documents:", error);
        setDocuments([]);
      } finally {
        if (!silent) {
          setLoadingDocuments(false);
        }
      }
    },
    [
      setLoadingDocuments,
      setDocuments,
      fetchDocumentThumbnail,
      mandant.SubjektId,
    ]
  );

  useEffect(() => {
    // Only fetch documents if mandant is activated
    if (mandant.activated === true) {
      fetchDocuments();
    }
  }, [mandant.activated, fetchDocuments]);

  // Check mandant activation status after all hooks are declared
  if (mandant.agbAccepted === true && mandant.activated === false) {
    return (
      <NotActiveSteuerKonto
        setMandant={setMandant}
        mandantInfo={mandant}
        documentsView={true}
        belegboxView={true}
      />
    );
  }

  if (mandant.agbAccepted === false && mandant.activated === false) {
    return <NotActiveMessage mandant={mandant} belegbox={true} />;
  }

  const fetchFullDocument = async (documentId) => {
    try {
      setLoadingFullDocument(true);
      const response = await HttpService.getAxiosClient().get(
        `/api/documents/get/${mandant.SubjektId}/${documentId}`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const contentType =
          response.headers["content-type"] || response.headers["Content-Type"];
        const fullDocumentUrl = URL.createObjectURL(response.data);

        setFullDocumentUrls((prev) => ({
          ...prev,
          [documentId]: {
            url: fullDocumentUrl,
            contentType: contentType,
          },
        }));
      }
    } catch (error) {
      console.error(`Error fetching full document ${documentId}:`, error);
    } finally {
      setLoadingFullDocument(false);
    }
  };

  const getTaxYearOptions = () => {
    const years = new Set();
    years.add("all");
    documents.forEach((doc) => {
      const taxYearProperty = doc.properties?.find(
        (prop) => prop.name === "taxYear"
      );
      if (taxYearProperty) {
        try {
          const year = JSON.parse(taxYearProperty.value);
          if (year) {
            years.add(year.toString());
          }
        } catch (e) {}
      }
    });

    const yearArray = Array.from(years);
    const allOption = yearArray.find((y) => y === "all");
    const yearOptions = yearArray
      .filter((y) => y !== "all")
      .sort((a, b) => b - a);

    return [
      { key: allOption || "all", text: "alle" },
      ...yearOptions.map((year) => ({ key: year, text: year })),
    ];
  };

  const handleDeleteDocument = async (documentId) => {
    const originalDocuments = [...documents];
    setDocuments((prev) =>
      prev.filter((doc) => doc.document_id !== documentId)
    );

    try {
      const response = await HttpService.getAxiosClient().delete(
        `/api/documents/delete/${mandant.SubjektId}/${documentId}`
      );

      if (response.status === 200) {
        setThumbnails((prev) => {
          const newThumbnails = { ...prev };
          delete newThumbnails[documentId];
          return newThumbnails;
        });
        // Clean up full document URL as well
        setFullDocumentUrls((prev) => {
          const newUrls = { ...prev };
          if (newUrls[documentId]) {
            URL.revokeObjectURL(newUrls[documentId].url);
            delete newUrls[documentId];
          }
          return newUrls;
        });
      } else {
        setDocuments(originalDocuments);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      setDocuments(originalDocuments);
    }
  };

  const openDeleteDialog = (documentId) => {
    setDocumentIdToDelete(documentId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (documentIdToDelete) {
      await handleDeleteDocument(documentIdToDelete);
    }
    setIsDeleteDialogOpen(false);
    setDocumentIdToDelete(null);
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDocumentIdToDelete(null);
  };

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `/api/documents/get/${mandant.SubjektId}/${documentId}`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      // TODO: Show error message to user
    }
  };

  const handleOpenDocumentInNewTab = async (documentId, fileName) => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `/api/documents/get/${mandant.SubjektId}/${documentId}`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        const contentType =
          response.headers["content-type"] || response.headers["Content-Type"];
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const newTab = window.open(url, "_blank", "noopener,noreferrer");
        if (newTab) {
          setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        } else {
          // If popup was blocked, revoke immediately
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error("Error opening document in new tab:", error);
    }
  };

  const handleEditDocument = (document) => {
    setSelectedDocument(document);
    setEditDescription(document.description);
    setEditFileName(document.file_name);
    const taxYearProperty = document.properties?.find(
      (prop) => prop.name === "taxYear"
    );
    const docTaxYear = taxYearProperty
      ? JSON.parse(taxYearProperty.value)
      : NEWEST_SELECTABLE_TAX_YEAR;
    setEditTaxYear(docTaxYear.toString());
    setIsEditDialogOpen(true);

    // Fetch the full document for display in the edit dialog
    if (!fullDocumentUrls[document.document_id]) {
      fetchFullDocument(document.document_id);
    }
  };

  const getEditTaxYearOptions = () =>
    SELECTABLE_TAX_YEARS.map((year) => ({
      key: year.toString(),
      text: year.toString(),
    }));

  const handleSaveEdit = async () => {
    if (!selectedDocument) return;

    try {
      const properties = {
        taxYear: parseInt(editTaxYear),
        uploadedAt:
          selectedDocument.properties?.find(
            (prop) => prop.name === "uploadedAt"
          )?.value || new Date().toISOString(),
      };

      const updatePayload = {
        SubjektId: parseInt(mandant.SubjektId),
        description: editDescription,
        fileName: editFileName,
        mimeType: selectedDocument.mime_type,
        properties: properties,
      };

      const response = await HttpService.getAxiosClient().post(
        `/api/documents/update/${mandant.SubjektId}/${selectedDocument.document_id}`,
        updatePayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Close the dialog and refresh the document list
        setIsEditDialogOpen(false);
        setSelectedDocument(null);
        setEditDescription("");
        setEditTaxYear("");
        setEditFileName("");

        // Refresh the document list to show updated data without flashing loader
        await fetchDocuments({ silent: true });
      }
    } catch (error) {
      console.error("Error updating document:", error);
      // TODO: Show error message to user
    }
  };

  const documentMenuProps = {
    items: [
      {
        key: "details",
        text: "Belegdetails bearbeiten",
        iconProps: { iconName: "Edit" },
        onClick: (e, item) => {
          const document = item.document;
          if (document) {
            handleEditDocument(document);
          }
        },
      },
      {
        key: "open",
        text: "Dokument anzeigen",
        iconProps: { iconName: "View" },
        onClick: (e, item) => {
          const documentId = item.documentId;
          const fileName = item.fileName;
          if (documentId && fileName) {
            handleOpenDocumentInNewTab(documentId, fileName);
          }
        },
      },
      {
        key: "download",
        text: "Beleg herunterladen",
        iconProps: { iconName: "Download" },
        onClick: (e, item) => {
          const documentId = item.documentId;
          const fileName = item.fileName;
          if (documentId && fileName) {
            handleDownloadDocument(documentId, fileName);
          }
        },
      },
      {
        key: "delete",
        text: "Beleg löschen",
        iconProps: { iconName: "Delete" },
        onClick: (e, item) => {
          const documentId = item.documentId;
          if (documentId) {
            openDeleteDialog(documentId);
          }
        },
      },
    ],
  };

  const filteredBelege = documents.filter((doc) => {
    const matchesSearch =
      searchText === "" ||
      doc.description.toLowerCase().includes(searchText.toLowerCase());

    const taxYearProperty = doc.properties?.find(
      (prop) => prop.name === "taxYear"
    );
    const docTaxYear = taxYearProperty ? JSON.parse(taxYearProperty.value) : "";

    const matchesTaxYear =
      selectedTaxYear === "all" || docTaxYear.toString() === selectedTaxYear;

    return matchesSearch && matchesTaxYear;
  });

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleOpenSnapsharePanel = () => {
    setIsSnapsharePanelOpen(true);
  };

  const handleCloseSnapsharePanel = () => {
    setIsSnapsharePanelOpen(false);
    fetchDocuments({ silent: true });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileChange({ target: { files } });
    }
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Validate file types
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
    ];

    const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".gif"];

    const invalidFiles = files.filter((file) => {
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      return (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      );
    });

    if (invalidFiles.length > 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFileTypeError(true);
      return;
    }

    // Clear any previous file type errors when valid files are selected
    setFileTypeError(false);

    setIsUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    try {
      // Upload files sequentially to avoid overwhelming the server
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length });
        const createResponse = await HttpService.getAxiosClient().post(
          "/api/documents/create",
          {
            SubjektId: parseInt(mandant.SubjektId),
            description: file.name,
            fileName: file.name,
            mimeType: file.type,
            properties: {
              taxYear: new Date().getFullYear(),
              uploadedAt: new Date().toISOString(),
            },
          }
        );

        if (createResponse.status === 200) {
          const { documentId } = createResponse.data;
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const uploadResponse = await HttpService.getAxiosClient().post(
            `/api/documents/upload/${mandant.SubjektId}/${documentId}`,
            file,
            {
              headers: {
                "Content-Type": "application/octet-stream",
              },
              transformRequest: [(data) => data],
            }
          );

          if (uploadResponse.status !== 200) {
            console.error(`Failed to upload file: ${file.name}`);
          }
        }
      }

      // Refresh the document list after all uploads are complete
      await fetchDocuments({ silent: true });
    } catch (error) {
      console.error("Error uploading files:", error);
      // TODO: Show error message to user
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <MainContentContainer>
      <ContentContainer className="vertical l-flex-rowgap-16">
        {fileTypeError && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setFileTypeError(false)}
            dismissButtonAriaLabel="Close"
          >
            Ungültiger Dateityp. Bitte laden Sie nur Dateien im Format PDF, JPG
            oder PNG hoch.
          </MessageBar>
        )}
        {/* Filter and Upload Section */}
        <ContentContainer className="vertical l-flex-rowgap-16">
          {/* Drag and Drop Area - Full Width */}
          <div
            style={{
              width: "100%",
            }}
          >
            <div
              style={{
                border: `1px dashed ${
                  isDragOver
                    ? "var(--themePrimary)"
                    : isHovered
                    ? "var(--neutralSecondary)"
                    : "var(--neutralTertiaryAlt)"
                }`,
                backgroundColor: isDragOver
                  ? "var(--neutralLighter)"
                  : "var(--white)",
                minHeight: "140px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                padding: "16px",
              }}
              onClick={handleFileSelect}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {isUploading ? (
                <ContentContainer
                  className="horizontal l-flex-align-center l-flex-justify-center l-flex-rowgap-4"
                  style={{ textAlign: "center" }}
                >
                  <Spinner size={SpinnerSize.medium} />
                  <Text
                    variant="large"
                    style={{
                      color: "var(--themePrimary)",
                    }}
                  >
                    Wird hochgeladen... {uploadProgress.current}/
                    {uploadProgress.total}
                  </Text>
                </ContentContainer>
              ) : (
                <>
                  <Text
                    variant="large"
                    style={{
                      color: isDragOver ? "var(--themePrimary)" : "#000000",
                      textAlign: "center",
                    }}
                  >
                    Ziehen Sie Belege hierher oder klicken Sie zum Hochladen
                    <br />
                    (PDF, JPG, PNG)
                  </Text>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "8px",
                      width: "100%",
                      maxWidth: "400px",
                    }}
                  >
                    <DefaultButton
                      text={
                        isUploading
                          ? `Wird hochgeladen... ${uploadProgress.current}/${uploadProgress.total}`
                          : "Belege hochladen"
                      }
                      iconProps={
                        !isUploading ? ICON_PROPS.UploadIcon : undefined
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileSelect();
                      }}
                      disabled={isUploading}
                      onRenderIcon={
                        isUploading
                          ? () => <Spinner size={SpinnerSize.small} />
                          : undefined
                      }
                      style={{ flex: 1 }}
                    />

                    <DefaultButton
                      text="SNAP.SHARE"
                      iconProps={{ iconName: "QRCode" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenSnapsharePanel();
                      }}
                      disabled={isUploading}
                      style={{ flex: 1 }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Search and Tax Year Fields */}
          <ContentContainer
            className="horizontal l-flex-rowgap-16 l-flex-align-end"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Suche"
              value={searchText}
              onChange={(e, newValue) => setSearchText(newValue || "")}
              style={{
                flex: "1 1 300px",
                minWidth: "250px",
              }}
            />
            <Dropdown
              label="Steuerjahr"
              options={getTaxYearOptions()}
              selectedKey={selectedTaxYear}
              onChange={(e, option) => setSelectedTaxYear(option?.key || "all")}
              style={{
                flex: "1 1 200px",
                minWidth: "150px",
              }}
            />
            <TooltipHost content="Liste neu laden">
              <DefaultButton
                iconProps={{ iconName: "Refresh" }}
                onClick={() => fetchDocuments()}
                disabled={loadingDocuments}
                ariaLabel="Liste neu laden"
                style={{ height: "44px", width: "44px", minWidth: "44px" }}
              />
            </TooltipHost>
          </ContentContainer>
        </ContentContainer>

        {/* Document List */}
        <ObjectList
          title="BelegBox"
          headingTag="h3"
          headingTagClass="title2"
          listHeader={
            <ObjectListHeader hasActionButton={true}>
              <Text as="span" className="flexItem t-listHeading l-fb-10">
                Vorschau
              </Text>
              <Text as="span" className="flexItem t-listHeading l-fb-10">
                Steuerjahr
              </Text>
              <Text as="span" className="flexItem t-listHeading l-fb-60">
                Bezeichnung
              </Text>
              <Text
                as="span"
                className="flexItem t-listHeading t-right l-fb-20"
              >
                Erfasst am
              </Text>
            </ObjectListHeader>
          }
        >
          {loadingDocuments && documents.length === 0 ? (
            <Text style={{ padding: "16px", color: "var(--neutralSecondary)" }}>
              Dokumente werden geladen...
            </Text>
          ) : filteredBelege.length === 0 ? (
            <ObjectItem
              className="infoWarning"
              ariaLabel="Information zur leeren Liste"
            >
              <ContentContainer className="horizontal">
                <FontIcon
                  iconName={ICON_PROPS.InfoIcon.iconName}
                  className="objectItemIcon"
                />
                <ContentContainer className="l-flex-column l-flex-align-start l-flex-rowgap-8">
                  <Text>Keine Dokumente gefunden.</Text>
                </ContentContainer>
              </ContentContainer>
            </ObjectItem>
          ) : (
            filteredBelege.map((doc) => {
              const taxYearProperty = doc.properties?.find(
                (prop) => prop.name === "taxYear"
              );
              const docTaxYear = taxYearProperty
                ? JSON.parse(taxYearProperty.value)
                : "";

              const createdDate = new Date(doc.created_at).toLocaleDateString(
                "de-DE"
              );

              const menuPropsWithId = {
                ...documentMenuProps,
                items: documentMenuProps.items.map((item) => ({
                  ...item,
                  documentId: doc.document_id,
                  fileName: doc.file_name,
                  document: doc,
                })),
              };

              return (
                <ObjectItem
                  key={doc.document_id}
                  onClick={() =>
                    console.log("Document clicked:", doc.document_id)
                  }
                  actionButton={
                    <IconButton
                      ariaLabel="IconButton with Menu"
                      className="moreIcon"
                      iconProps={null}
                      menuIconProps={{ iconName: "menuDotsIcon" }}
                      menuProps={menuPropsWithId}
                    />
                  }
                >
                  <ContentContainer className="l-flex-rowgap-8">
                    <ContentContainer size="l-fb-10" className="horizontal">
                      <Text
                        as="span"
                        className="flexItem t-listText hiddenLabel"
                      >
                        Vorschau:
                      </Text>
                      {thumbnails[doc.document_id] ? (
                        <img
                          src={thumbnails[doc.document_id]}
                          alt="Thumbnail"
                          style={{
                            width: "32px",
                            height: "40px",
                            borderRadius: "1px",
                            objectFit: "cover",
                            objectPosition: "top",
                            boxShadow:
                              "0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
                            backgroundColor: "white",
                          }}
                        />
                      ) : (
                        <Spinner size={SpinnerSize.small} />
                      )}
                    </ContentContainer>
                    <ContentContainer size="l-fb-10" className="horizontal">
                      <Text
                        as="span"
                        className="flexItem t-listText hiddenLabel"
                      >
                        Steuerjahr:
                      </Text>
                      <Text as="span" className="flexItem t-listText t-right-s">
                        {docTaxYear}
                      </Text>
                    </ContentContainer>
                    <ContentContainer
                      size="l-fb-60"
                      className="horizontal t-ellipsis l-flex-orderSwitch-1-s"
                    >
                      <Text
                        as="span"
                        className="flexItem t-listText visuallyHidden"
                      >
                        Bezeichnung:
                      </Text>
                      <Text as="span" className="flexItem t-listText title4-s">
                        {doc.description}
                      </Text>
                    </ContentContainer>
                    <ContentContainer
                      size="l-fb-20"
                      className="horizontal t-ellipsis"
                    >
                      <Text
                        as="span"
                        className="flexItem t-listText hiddenLabel"
                      >
                        Erstellt am:
                      </Text>
                      <Text as="span" className="flexItem t-listText t-right">
                        {createdDate}
                      </Text>
                    </ContentContainer>
                  </ContentContainer>
                </ObjectItem>
              );
            })
          )}
        </ObjectList>

        {/* Edit Document Dialog */}
        <Dialog
          hidden={!isEditDialogOpen}
          onDismiss={() => setIsEditDialogOpen(false)}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Belegdetails bearbeiten",
          }}
          minWidth={600}
        >
          <ContentContainer
            className="horizontal l-flex-rowgap-16"
            style={{ padding: "16px 0" }}
          >
            {/* Left side - Document preview */}
            <ContentContainer className="vertical l-fb-40">
              <Text variant="medium" style={{ marginBottom: "8px" }}>
                Dokumentvorschau
              </Text>
              <ContentContainer
                className="vertical l-flex-justify-center l-flex-align-center"
                style={{
                  border: "1px solid var(--neutralTertiaryAlt)",
                  borderRadius: "4px",
                  minHeight: "200px",
                  backgroundColor: "var(--neutralLighterAlt)",
                  padding: "4px",
                }}
              >
                {selectedDocument &&
                fullDocumentUrls[selectedDocument.document_id] ? (
                  (() => {
                    const documentData =
                      fullDocumentUrls[selectedDocument.document_id];
                    const isPdf =
                      documentData.contentType === "application/pdf";

                    if (isPdf) {
                      return (
                        <div
                          style={{
                            width: "200px",
                            height: "220px",
                            backgroundColor: "white",
                            boxShadow:
                              "0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden",
                            margin: "0 auto",
                          }}
                        >
                          <iframe
                            src={documentData.url}
                            width="200px"
                            height="220px"
                            style={{
                              border: "none",
                              borderRadius: "2px",
                            }}
                            title="PDF"
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div
                          style={{
                            width: "150px",
                            height: "200px",
                            backgroundColor: "white",
                            boxShadow:
                              "0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden",
                            margin: "0 auto",
                          }}
                        >
                          <img
                            src={documentData.url}
                            alt="Document preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      );
                    }
                  })()
                ) : loadingFullDocument ? (
                  <div
                    style={{
                      width: "150px",
                      height: "200px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "0 auto",
                    }}
                  >
                    <Spinner size={SpinnerSize.medium} />
                  </div>
                ) : (
                  <>
                    <Text style={{ color: "var(--neutralSecondary)" }}>
                      {selectedDocument?.file_name || "Dokument"}
                    </Text>
                    <Text
                      variant="small"
                      style={{
                        color: "var(--neutralSecondary)",
                        marginTop: "4px",
                      }}
                    >
                      {selectedDocument?.mime_type || "application/pdf"}
                    </Text>
                  </>
                )}
              </ContentContainer>
            </ContentContainer>

            {/* Right side - Edit form */}
            <ContentContainer className="vertical l-fb-60 l-flex-rowgap-16">
              <TextField
                label="Bezeichnung"
                value={editDescription}
                onChange={(e, newValue) => setEditDescription(newValue || "")}
              />
              <TextField
                label="Dateiname"
                readOnly={true}
                value={editFileName}
                onChange={(e, newValue) => setEditFileName(newValue || "")}
              />
              <Dropdown
                label="Steuerjahr"
                options={getEditTaxYearOptions()}
                selectedKey={editTaxYear}
                onChange={(e, option) => setEditTaxYear(option?.key || "")}
              />
            </ContentContainer>
          </ContentContainer>
          <DialogFooter>
            <PrimaryButton
              text="Speichern"
              onClick={handleSaveEdit}
              disabled={!editDescription.trim() || !editFileName.trim()}
            />
            <DefaultButton
              text="Abbrechen"
              onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedDocument(null);
                setEditDescription("");
                setEditTaxYear("");
                setEditFileName("");
              }}
            />
          </DialogFooter>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog
          hidden={!isDeleteDialogOpen}
          onDismiss={cancelDelete}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Wollen Sie diesen Beleg wirklich löschen?",
          }}
          minWidth={400}
        >
          <DialogFooter>
            <PrimaryButton text="Löschen" onClick={confirmDelete} />
            <DefaultButton text="Abbrechen" onClick={cancelDelete} />
          </DialogFooter>
        </Dialog>

        <SnapsharePanel
          mandant={mandant}
          isOpen={isSnapsharePanelOpen}
          onDismiss={handleCloseSnapsharePanel}
          onDocumentsFetched={() => fetchDocuments({ silent: true })}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept=".pdf,.jpg,.jpeg,.png,.gif"
        />
      </ContentContainer>
    </MainContentContainer>
  );
};
